import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { submitWizardData } from './wizardApi';
import { FormattedMessageProps } from 'types/utils';
import { ValidationResult } from 'types/validation';

interface WizardState {
    title: string | FormattedMessageProps;
    firstStepId: string;
    currentStepId: string;
    formData: Record<string, any>;
    payload: Record<string, any>;
    initialFormData: Record<string, any>;
    history: string[];
    isSubmitting: boolean;
    isValidating: boolean;
    isLoading: boolean;
    error: string | null;
    validationResults: Record<string, ValidationResult | undefined>;
    metadata?: Record<string, any>;
}

/**
 * wizardSlice manages the global Redux state for multi-step wizard workflows.
 *
 * Provides centralized state management for:
 * - Step navigation with history tracking for back/forward movement
 * - Form data persistence across steps (isolated per stepId)
 * - Validation state management for step-level feedback
 * - Async submission handling with loading states
 * - Wizard configuration and metadata storage
 *
 * Key actions: startWizard, goToStep, goBack, updateStepData, resetWizard
 * Used by: useWizard hook, WizardContainer, step components
 */
const initialState: WizardState = {
    title: '',
    firstStepId: '',
    currentStepId: '',
    formData: {},
    payload: {},
    initialFormData: {},
    history: [],
    isSubmitting: false,
    isValidating: false,
    isLoading: true,
    error: null,
    validationResults: {},
    metadata: {},
};

export const wizardSlice = createSlice({
    name: 'wizard-slice',
    initialState,
    reducers: {
        startWizard: (
            state,
            action: PayloadAction<{
                title: string | FormattedMessageProps;
                firstStepId: string;
                initialFormData?: any;
                metadata?: any;
            }>
        ) => {
            try {
                const { title, firstStepId, initialFormData, metadata } = action.payload;

                state.title = title;
                state.firstStepId = firstStepId;
                state.currentStepId = firstStepId;
                state.history = [firstStepId];
                state.formData = initialFormData ?? {};
                state.payload = {};
                state.initialFormData = initialFormData ?? {};
                state.isSubmitting = false;
                state.error = null;
                state.isLoading = false;
                state.metadata = metadata ?? {};
            } catch (error) {
                state.error = error instanceof Error ? error.message : 'Unknown error';
                state.isLoading = false;
            }
        },

        goToStep: (state, action: PayloadAction<{ stepId: string }>) => {
            state.currentStepId = action.payload.stepId;
            state.history.push(action.payload.stepId);
        },

        goBack: (state) => {
            if (state.history.length > 1) {
                const stepId = state.history.pop()!;
                delete state.validationResults[stepId];
                delete state.formData[stepId];
                state.currentStepId = state.history[state.history.length - 1];
            }
        },
        updateStepData: (state, action: PayloadAction<{ stepId: string; data: any }>) => {
            const { stepId, data } = action.payload;
            state.formData[stepId] = data;
        },

        setPayload: (state, action: PayloadAction<Record<string, any>>) => {
            state.payload = action.payload;
        },

        updateWizardTitle: (state, action: PayloadAction<string>) => {
            state.title = action.payload;
        },

        resetWizard: (state) => {
            state.currentStepId = state.firstStepId;
            state.formData = state?.initialFormData ?? {};
            state.history = [state.firstStepId];
            state.isSubmitting = false;
            state.error = null;
            state.isLoading = false;
            state.validationResults = {};
        },

        setValidationResults: (state, action: PayloadAction<Record<string, ValidationResult | undefined>>) => {
            state.validationResults = action.payload;
        },

        setCurrentStepValidationResult: (
            state,
            action: PayloadAction<{ stepId: string; validation: ValidationResult }>
        ) => {
            const { stepId, validation } = action.payload;
            state.validationResults[stepId] = validation;
        },
        setIsSubmitting: (state, action: PayloadAction<boolean>) => {
            state.isSubmitting = action.payload;
        },
        setMetadata: (state, action: PayloadAction<Record<string, any>>) => {
            state.metadata = action.payload;
        },
        setIsValidating: (state, action: PayloadAction<boolean>) => {
            state.isValidating = action.payload;
        },
    },

    extraReducers: (builder) => {
        builder.addCase(submitWizardData.pending, (state) => {
            state.isSubmitting = true;
            state.error = null;
        });
        builder.addCase(submitWizardData.fulfilled, (state) => {
            state.isSubmitting = false;
            state.error = null;
            state.currentStepId = state.firstStepId;
            state.formData = state?.initialFormData ?? {};
            state.history = [state.firstStepId];
            state.validationResults = {};
        });
        builder.addCase(submitWizardData.rejected, (state, action) => {
            state.isSubmitting = false;
            state.error = action.error.message || 'Error submitting wizard data';
        });
    },
});

export const {
    startWizard,
    goToStep,
    goBack,
    updateStepData,
    resetWizard,
    updateWizardTitle,
    setCurrentStepValidationResult,
    setValidationResults,
    setIsSubmitting,
    setMetadata,
    setPayload,
    setIsValidating,
} = wizardSlice.actions;
